import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AppLayout } from '@/components/Layout';
import HighPriorityBanner from '@/components/HighPriorityBanner';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
// REMOVED: AdminDashboard - merged into Sales Intelligence
import AdminUsers from '@/pages/AdminUsers';
import AdminAnalytics from '@/pages/AdminAnalytics';
import AccountabilitySnapshot from '@/pages/AccountabilitySnapshot';
import Leaderboard from '@/pages/Leaderboard';
import Feed from '@/pages/Feed';
import Chat from '@/pages/Chat';
// REMOVED: AddBooking, MyBookings - Booking monitoring via My Performance
import CreateBooking from '@/pages/CreateBooking';
import IncentiveCalculator from '@/pages/IncentiveCalculator';
import EMICalculator from '@/pages/EMICalculator';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import AdminSlabs from '@/pages/AdminSlabs';
import AdminProjects from '@/pages/AdminProjects';
import CRM from '@/pages/CRM';
import AddCustomer from '@/pages/AddCustomer';
import CustomerDetail from '@/pages/CustomerDetail';
import CustomerDetailRep from '@/pages/CustomerDetailRep';
import FollowUps from '@/pages/FollowUps';
import HotDeals from '@/pages/HotDeals';
import BookingVerification from '@/pages/BookingVerification';
import BookingDetail from '@/pages/BookingDetail';
// REMOVED: BookingFunnelDashboard - merged into Sales Intelligence as "Pipeline Health" tab
import CancellationHistory from '@/pages/CancellationHistory';
import Profile from '@/pages/Profile';
import ReferralDashboard from '@/pages/ReferralDashboard';
import VDNBManagement from '@/pages/VDNBManagement';
import CommandCenter from '@/pages/CommandCenter';
import SAIRA from '@/pages/SAIRA';
import BDDashboard from '@/pages/BDDashboard';
import BDRevenueDashboard from '@/pages/BDRevenueDashboard';
import BuildersList from '@/pages/BuildersList';
import BuilderDetail from '@/pages/BuilderDetail';
import BDPipeline from '@/pages/BDPipeline';
import BDProjectForm from '@/pages/BDProjectForm';
import BDMeetingForm from '@/pages/BDMeetingForm';
import BDAnalytics from '@/pages/BDAnalytics';
import BDRevenuePipeline from '@/pages/BDRevenuePipeline';
import MarketIntelligence from '@/pages/MarketIntelligence';
import RepPerformance from '@/pages/RepPerformance';
import Notifications from '@/pages/Notifications';
import CityHeadDashboard from '@/pages/CityHeadDashboard';
import ManagerDashboard from '@/pages/ManagerDashboard';
import ManagerDesk from '@/pages/ManagerDesk';
import MyTasks from '@/pages/MyTasks';
import AuctionSupply from '@/pages/AuctionSupply';
import './App.css';

// Auth Provider Component
function AuthProvider({ children, user, setUser, loading, setLoading }) {
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    
    // No token stored - not authenticated
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        // Token invalid or expired - clear and redirect to login
        console.log('Auth check failed with status:', response.status);
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Auth check network error:', error);
      // On network error, try to use cached user if available
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        try {
          setUser(JSON.parse(cachedUser));
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return children;
}

// Protected Route Component - wraps Rep pages with AppLayout
function ProtectedRoute({ children, user, loading, noLayout = false }) {
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B3DF5] mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If user data passed from AuthCallback, use it
  if (location.state?.user) {
    return noLayout ? children : <AppLayout user={location.state.user}>{children}</AppLayout>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return noLayout ? children : <AppLayout user={user}>{children}</AppLayout>;
}

// Admin Route Component - Now uses AppLayout for unified navigation
function AdminRoute({ children, user, loading }) {
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EF4444] mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Include all admin roles including strategy_support_admin and crm_support
  const adminRoles = ['Admin', 'super_admin', 'city_admin', 'strategy_support_admin', 'crm_support', 'Manager'];
  const isAdminUser = user && adminRoles.includes(user.role);
  
  if (!isAdminUser) {
    return <Navigate to="/" replace />;
  }
  
  // Wrap admin pages with AppLayout for unified navigation
  return <AppLayout user={user}>{children}</AppLayout>;
}

// Home Route - Renders role-based dashboard within unified layout
function HomeRoute({ user, loading }) {
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EF4444] mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  
  // All users get unified layout, dashboard content varies by role
  // Admin/super_admin/city_admin/crm_support all see Sales Intelligence Dashboard (CityHeadDashboard)
  // CRM Support sees All Bookings tab by default (via query param)
  // Regular reps see their individual Dashboard
  const isAdminOrCrm = ['Admin', 'super_admin', 'city_admin', 'crm_support'].includes(user.role);
  
  return (
    <AppLayout user={user}>
      {isAdminOrCrm ? (
        // CRM Support users are redirected to All Bookings view
        user.role === 'crm_support' ? (
          <Navigate to="/city-head-dashboard?tab=bookings" replace />
        ) : (
          <CityHeadDashboard user={user} />
        )
      ) : (
        <Dashboard user={user} />
      )}
    </AppLayout>
  );
}

// App Router - Handles session_id detection
function AppRouter({ user, setUser, loading, setLoading }) {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }

  return (
    <AuthProvider user={user} setUser={setUser} loading={loading} setLoading={setLoading}>
      <Routes>
        <Route path="/login" element={<Login setUser={setUser} />} />
        <Route path="/" element={<HomeRoute user={user} loading={loading} />} />
        <Route path="/notifications" element={<ProtectedRoute user={user} loading={loading}><Notifications user={user} /></ProtectedRoute>} />
        <Route path="/performance" element={<ProtectedRoute user={user} loading={loading}><RepPerformance user={user} /></ProtectedRoute>} />
        <Route path="/rep-dashboard" element={<ProtectedRoute user={user} loading={loading}><RepPerformance user={user} /></ProtectedRoute>} />
        {/* REMOVED: admin-dashboard route - now redirects to Sales Intelligence */}
        <Route path="/admin-dashboard" element={<Navigate to="/city-head-dashboard" replace />} />
        <Route path="/admin/users" element={<AdminRoute user={user} loading={loading}><AdminUsers user={user} /></AdminRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute user={user} loading={loading}><Leaderboard user={user} /></ProtectedRoute>} />
        <Route path="/feed" element={<ProtectedRoute user={user} loading={loading}><Feed user={user} /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute user={user} loading={loading}><Chat user={user} /></ProtectedRoute>} />
        <Route path="/chat/:userId" element={<ProtectedRoute user={user} loading={loading}><Chat user={user} /></ProtectedRoute>} />
        {/* DEPRECATED: add-booking - reps create bookings from CRM → Customer → Add Booking */}
        <Route path="/add-booking" element={<Navigate to="/performance" replace />} />
        {/* DEPRECATED: my-bookings standalone page - use My Performance → My Bookings instead */}
        <Route path="/my-bookings" element={<Navigate to="/performance" replace />} />
        <Route path="/incentive" element={<ProtectedRoute user={user} loading={loading}><IncentiveCalculator user={user} /></ProtectedRoute>} />
        <Route path="/emi-calculator" element={<ProtectedRoute user={user} loading={loading}><EMICalculator user={user} /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute user={user} loading={loading}><Projects user={user} /></ProtectedRoute>} />
        <Route path="/projects/:projectId" element={<ProtectedRoute user={user} loading={loading}><ProjectDetail user={user} /></ProtectedRoute>} />
        <Route path="/admin/slabs" element={<AdminRoute user={user} loading={loading}><AdminSlabs user={user} /></AdminRoute>} />
        <Route path="/admin/projects" element={<AdminRoute user={user} loading={loading}><AdminProjects user={user} /></AdminRoute>} />
        <Route path="/admin/analytics" element={<AdminRoute user={user} loading={loading}><AdminAnalytics user={user} /></AdminRoute>} />
        <Route path="/admin/accountability" element={<AdminRoute user={user} loading={loading}><AccountabilitySnapshot user={user} /></AdminRoute>} />
        {/* DEPRECATED: booking-verification - redirect to Sales Intelligence All Bookings */}
        <Route path="/admin/booking-verification" element={<Navigate to="/city-head-dashboard?tab=bookings" replace />} />
        {/* REMOVED: booking-funnel route - merged into Sales Intelligence "Pipeline Health" tab */}
        <Route path="/admin/booking-funnel" element={<Navigate to="/city-head-dashboard" replace />} />
        <Route path="/booking-funnel" element={<Navigate to="/city-head-dashboard" replace />} />
        <Route path="/admin/cancellation-history" element={<AdminRoute user={user} loading={loading}><CancellationHistory user={user} /></AdminRoute>} />
        <Route path="/admin/referrals" element={<AdminRoute user={user} loading={loading}><ReferralDashboard user={user} /></AdminRoute>} />
        <Route path="/admin/vdnb" element={<AdminRoute user={user} loading={loading}><VDNBManagement user={user} /></AdminRoute>} />
        <Route path="/admin/command-center" element={<AdminRoute user={user} loading={loading}><CommandCenter user={user} /></AdminRoute>} />
        <Route path="/admin/sales-intelligence" element={<AdminRoute user={user} loading={loading}><CityHeadDashboard user={user} /></AdminRoute>} />
        {/* Supply Module - Admin Only */}
        <Route path="/admin/supply/auctions" element={<AdminRoute user={user} loading={loading}><AuctionSupply user={user} /></AdminRoute>} />
        {/* Sales Intelligence - Primary Dashboard for City Admin */}
        <Route path="/city-head-dashboard" element={<AdminRoute user={user} loading={loading}><CityHeadDashboard user={user} /></AdminRoute>} />
        <Route path="/manager-dashboard" element={<AdminRoute user={user} loading={loading}><ManagerDashboard user={user} /></AdminRoute>} />
        {/* Manager Desk - Tasks, Followups, Diary */}
        <Route path="/manager-desk" element={<AdminRoute user={user} loading={loading}><ManagerDesk user={user} /></AdminRoute>} />
        {/* My Tasks - Accessible to ALL users */}
        <Route path="/my-tasks" element={<ProtectedRoute user={user} loading={loading}><MyTasks user={user} /></ProtectedRoute>} />
        {/* BD Revenue Tracker */}
        <Route path="/bd-dashboard" element={<ProtectedRoute user={user} loading={loading}><BDRevenueDashboard user={user} /></ProtectedRoute>} />
        <Route path="/saira" element={<ProtectedRoute user={user} loading={loading}><SAIRA user={user} /></ProtectedRoute>} />
        <Route path="/crm" element={<ProtectedRoute user={user} loading={loading}><CRM user={user} /></ProtectedRoute>} />
        <Route path="/crm/add" element={<ProtectedRoute user={user} loading={loading}><AddCustomer user={user} /></ProtectedRoute>} />
        <Route path="/crm/:customerId" element={
          <ProtectedRoute user={user} loading={loading}>
            {user?.role === 'Rep' ? <CustomerDetailRep user={user} /> : <CustomerDetail user={user} />}
          </ProtectedRoute>
        } />
        <Route path="/followups" element={<ProtectedRoute user={user} loading={loading}><FollowUps user={user} /></ProtectedRoute>} />
        <Route path="/hot-deals" element={<ProtectedRoute user={user} loading={loading}><HotDeals user={user} /></ProtectedRoute>} />
        {/* Booking creation from CRM - Uses full booking form with site visit validation */}
        <Route path="/bookings/new" element={<ProtectedRoute user={user} loading={loading}><CreateBooking user={user} /></ProtectedRoute>} />
        {/* DEPRECATED: my-bookings standalone page - use My Performance → My Bookings instead */}
        <Route path="/my-bookings" element={<Navigate to="/performance" replace />} />
        <Route path="/bookings/:bookingId" element={<ProtectedRoute user={user} loading={loading}><BookingDetail user={user} /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute user={user} loading={loading}><Profile user={user} /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute user={user} loading={loading}><Projects user={user} /></ProtectedRoute>} />
        
        {/* BD Desk Routes */}
        <Route path="/bd" element={<ProtectedRoute user={user} loading={loading}><BDDashboard user={user} /></ProtectedRoute>} />
        <Route path="/bd/builders" element={<ProtectedRoute user={user} loading={loading}><BuildersList user={user} /></ProtectedRoute>} />
        <Route path="/bd/builders/:builderId" element={<ProtectedRoute user={user} loading={loading}><BuilderDetail user={user} /></ProtectedRoute>} />
        <Route path="/bd/pipeline" element={<ProtectedRoute user={user} loading={loading}><BDPipeline user={user} /></ProtectedRoute>} />
        <Route path="/bd/projects/:projectId" element={<ProtectedRoute user={user} loading={loading}><BDProjectForm user={user} /></ProtectedRoute>} />
        <Route path="/bd/meetings/new" element={<ProtectedRoute user={user} loading={loading}><BDMeetingForm user={user} /></ProtectedRoute>} />
        <Route path="/bd/meetings/:meetingId" element={<ProtectedRoute user={user} loading={loading}><BDMeetingForm user={user} /></ProtectedRoute>} />
        <Route path="/bd/analytics" element={<ProtectedRoute user={user} loading={loading}><BDAnalytics user={user} /></ProtectedRoute>} />
        <Route path="/bd/revenue" element={<ProtectedRoute user={user} loading={loading}><BDRevenuePipeline user={user} /></ProtectedRoute>} />
        <Route path="/market-intelligence" element={<ProtectedRoute user={user} loading={loading}><MarketIntelligence user={user} /></ProtectedRoute>} />
      </Routes>
    </AuthProvider>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter 
          user={user} 
          setUser={setUser} 
          loading={loading} 
          setLoading={setLoading} 
        />
        {/* High Priority Notification Banner - Shows on all pages */}
        {user && <HighPriorityBanner user={user} />}
      </BrowserRouter>
      <Toaster position="top-center" richColors toastOptions={{ className: 'mt-12' }} />
    </div>
  );
}

export default App;
